/**
 * Donut cards for Live Game View and Game Results — one copy, both pages.
 *
 * Every slice is its own colour with a matching coloured pill underneath.
 * Tapping either the slice or the pill filters the whole page; tapping the
 * same one again clears it. The donuts are built from the rows the page
 * already has, so a live refresh never resets a filter.
 */
import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { GamePill, TONES, TONE_CYCLE, STATUS_TONE } from './GamePlayerChips';

const STATUSES = ['registered', 'newly_registered', 'new'];
const RESULTS = { win: 'green', lose: 'red', pending: 'amber' };
const VOUCHERS = { redeemed: 'green', unused: 'blue', expired: 'grey', none: 'grey' };

/** The team / side a winner pick names, e.g. "home (waiting)" → "home". */
export const pickKey = (row) => {
    const text = String(row?.winner_pick || '').trim();
    if (!text) return 'none';
    return text.split(' ')[0];
};

export const resultKey = (row) => {
    if (!row?.scratch_count) return 'pending';
    return row.result === 'win' ? 'win' : row.result === 'lose' ? 'lose' : 'pending';
};

export const voucherKey = (row) => row?.voucher_status || 'none';
export const prizeKey = (row) => row?.prize || 'none';
export const statusKey = (row) => row?.status || 'new';
export const giftCardKey = (row) => row?.gift_card_key || 'none';

/**
 * LIVE GAME VIEW ONLY — the return gift card card. Built from the ledger counts
 * the endpoint sends, not from the player rows, so it shows 0 with an empty
 * ring rather than disappearing. Game Results never asks for it.
 */
export const buildGiftCardDonut = (giftCards, t) => ({
    id: 'giftcard',
    label: t('gameBoard.donut.giftCard', 'Return gift cards'),
    slices: [
        { key: 'won_later', value: Number(giftCards?.won_later) || 0,
          label: t('gameBoard.giftCard.won_later', 'Won tonight, use later'),
          tone: TONES.fuchsia },
        { key: 'redeemed', value: Number(giftCards?.redeemed) || 0,
          label: t('gameBoard.giftCard.redeemed', 'Redeemed tonight'),
          tone: TONES.yellow },
    ],
});

const tally = (rows, keyOf) => {
    const out = new Map();
    rows.forEach((row) => {
        const key = keyOf(row);
        out.set(key, (out.get(key) || 0) + 1);
    });
    return out;
};

/**
 * The five donuts both pages draw, from the player rows themselves.
 * `rows` must be the UNFILTERED rows so a slice never disappears when it is
 * the one being filtered on.
 */
export const buildGameDonuts = (rows, t, options = {}) => {
    const withStatus = options.status !== false;
    // Live Game View keeps every card on the page even with nothing in it, so
    // the row is never lopsided. Game Results is unchanged: it still drops a
    // donut that has nothing to draw.
    const keepEmpty = options.keepEmpty === true;
    const donuts = [];
    const push = (id, labelKey, labelFallback, counts, toneFor, labelFor) => {
        const all = [...counts.entries()].map(([key, value], index) => ({
            key,
            value,
            label: labelFor(key),
            tone: TONES[toneFor(key, index)] || TONES.grey,
        }));
        const slices = all.filter((slice) => slice.value > 0);
        if (slices.length) {
            donuts.push({ id, label: t(labelKey, labelFallback), slices });
        } else if (keepEmpty) {
            // Zero everywhere: the pills still show, each reading 0.
            donuts.push({ id, label: t(labelKey, labelFallback), slices: all });
        }
    };

    const statusCounts = new Map(STATUSES
        .map((s) => [s, rows.filter((r) => statusKey(r) === s).length]));
    if (withStatus) {
        push('status', 'gameBoard.donut.status', 'Players by status', statusCounts,
            (key) => STATUS_TONE[key] || 'grey', (key) => t(`gameBoard.status.${key}`, key));
    }

    const resultCounts = new Map(['win', 'lose', 'pending']
        .map((k) => [k, rows.filter((r) => resultKey(r) === k).length]));
    push('result', 'gameBoard.donut.result', 'Results', resultCounts,
        (key) => RESULTS[key] || 'grey', (key) => t(`gameBoard.result.${key}`, key));

    const pickCounts = tally(rows.filter((r) => pickKey(r) !== 'none'), pickKey);
    push('pick', 'gameBoard.donut.pick', 'Picks by team', pickCounts,
        (key, index) => TONE_CYCLE[index % TONE_CYCLE.length],
        (key) => t(`gameBoard.pick.${key}`, key));

    const prizeCounts = tally(rows.filter((r) => prizeKey(r) !== 'none'), prizeKey);
    push('prize', 'gameBoard.donut.prize', 'Prizes given', prizeCounts,
        (key, index) => TONE_CYCLE[index % TONE_CYCLE.length], (key) => key);

    const voucherCounts = tally(rows.filter((r) => voucherKey(r) !== 'none'), voucherKey);
    if (keepEmpty && voucherCounts.size === 0) {
        ['redeemed', 'unused', 'expired'].forEach((k) => voucherCounts.set(k, 0));
    }
    push('voucher', 'gameBoard.donut.voucher', 'Vouchers', voucherCounts,
        (key) => VOUCHERS[key] || 'grey', (key) => t(`gameBoard.voucher.${key}`, key));

    return donuts;
};

const Donut = ({ donut, active, onToggle, prefix, emptyHint }) => {
    const total = useMemo(
        () => donut.slices.reduce((sum, s) => sum + s.value, 0), [donut.slices]);
    const chosen = active[donut.id] || [];

    return (
        <Card data-testid={`${prefix}-donut-${donut.id}`}>
            <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{donut.label}</p>
                {/* One short line instead of a blank card. It goes as soon as
                    the card has something to show, and the height it takes is
                    always reserved, so nothing jumps. */}
                <p className="h-4 mb-1 text-[11px] text-muted-foreground/70"
                   data-testid={`${prefix}-donut-${donut.id}-empty-hint`}>
                    {total === 0 ? (emptyHint || '') : ''}
                </p>
                <div className="h-[132px] relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            {total === 0 ? (
                                <Pie data={[{ key: 'empty', value: 1 }]} dataKey="value"
                                     innerRadius={38} outerRadius={60} stroke="none"
                                     isAnimationActive={false}>
                                    <Cell fill="currentColor" className="text-muted-foreground" opacity={0.18} />
                                </Pie>
                            ) : (
                            <Pie data={donut.slices} dataKey="value" nameKey="label"
                                 innerRadius={38} outerRadius={60} paddingAngle={2}
                                 stroke="none" isAnimationActive={false}
                                 onClick={(slice) => onToggle(donut.id, slice.key)}>
                                {donut.slices.map((slice) => (
                                    <Cell key={slice.key} fill={slice.tone.fg}
                                          cursor="pointer"
                                          opacity={chosen.length && !chosen.includes(slice.key) ? 0.28 : 1} />
                                ))}
                            </Pie>
                            )}
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="text-xl font-bold">{total}</span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-2">
                    {donut.slices.map((slice) => (
                        <GamePill key={slice.key} tone={slice.tone} label={slice.label}
                                  count={slice.value} selected={chosen.includes(slice.key)}
                                  onClick={() => onToggle(donut.id, slice.key)}
                                  testId={`${prefix}-pill-${donut.id}-${slice.key}`} />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export const GameDonuts = ({ donuts, active, onToggle, prefix, emptyHint }) => {
    if (!donuts.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3"
             data-testid={`${prefix}-donuts`}>
            {donuts.map((donut) => (
                <Donut key={donut.id} donut={donut} active={active}
                       onToggle={onToggle} prefix={prefix} emptyHint={emptyHint} />
            ))}
        </div>
    );
};
